import React, {
  useEffect,
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Grid,
  TableContainer,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Checkbox,
  Paper,
  Tooltip,
  Badge,
  IconButton,
} from "@mui/material";
import PropTypes from "prop-types";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useColumnFilter } from "../useColumnFilter";
import ColumnFilterPopover from "../ColumnFilterPopover";

import BackdropComponent from "../../../Spinner/Spinner";
import TextLabel from "components/Common/TextLabel/TextLabel";
import classes from "../../OMTBulkEdit/Common.module.css";
import UnfoldMoreOutlinedIcon from "@mui/icons-material/UnfoldMoreOutlined";
import {
  CustomButton,
  CustomTooltip,
} from "../../../../components/RosterConfig/RosterConfigStyle";
import SnackBar from "../../../../components/SnackBar/SnackBar";
import { fontStyles } from "components/Common/commonStyles";
import {
  getOMTCredentials,
  saveOMTCredentials,
} from "../../../../services/userServices";
import { AttachFileOutlined } from "@mui/icons-material";
import OMTAttachments from "../OMTAttachments";
import { red } from "@mui/material/colors";

const isLockedByHardware = (row) => {
  const reason = row.HardwareFailureReason;
  if (!reason) return false;
  if (reason.startsWith("Pre-pass")) return false;
  return true;
};

const Credentials = forwardRef(function Credentials(props, ref) {
  const {
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleType,
    editAccess,
    agentOracleId,
    onDirtyChange,
    noRosterIdClassName,
    isUploadMode,
    isNoRosterIdSearch,
  } = props;

  const [loading, setLoader] = useState(false);
  const [originalCredentialsData, setOriginalCredentialsData] = useState([]);
  const [order, setOrder] = useState("ASC");
  const [CredentialsData, setCredentialsData] = useState([]);
  const [openAttachmentDialog, setOpenAttachmentDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState();
  const isFirstRender = useRef(true);

  const handleAttachmentOpen = (row) => {
    setOpenAttachmentDialog(true);
    setSelectedRow(row);
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const snackReset = () => setSnack({ open: false, message: "", severity: "" });
  const errorHandler = (open, message, severity) =>
    setSnack({ open: true, message, severity });

  const isDirty =
    JSON.stringify(CredentialsData) !== JSON.stringify(originalCredentialsData);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);

  useImperativeHandle(ref, () => ({
    save: () => handleSave(),
  }));

  useEffect(() => {
    let cancelled = false;

    const loadCredentialsData = async () => {
      if (
        (rosterId !== null && rosterId !== "") ||
        (noRosterIdClassName !== null && noRosterIdClassName !== "") ||
        (agentOracleId !== null && agentOracleId !== "" && !noRosterIdClassName)
      ) {
        if (!cancelled) setCredentialsData([]);
        try {
          await fetchCredentialsData(
            rosterId,
            asdLeadOracleId,
            taskTypeId,
            scheduleType,
            agentOracleId,
            noRosterIdClassName,
            isNoRosterIdSearch,
          );
        } catch (error) {
          if (!cancelled)
            console.error("Failed to fetch credentials data:", error);
        }
      } else {
        setCredentialsData([]);
      }
    };

    loadCredentialsData();

    return () => {
      cancelled = true;
    };
  }, [rosterId, agentOracleId, noRosterIdClassName]);

  useEffect(() => {
    let cancelled = false;

    const refreshAfterAttachment = async () => {
      if (isFirstRender.current) {
        isFirstRender.current = false;
        return;
      }

      if (openAttachmentDialog === false) {
        try {
          await fetchCredentialsData(
            rosterId,
            asdLeadOracleId,
            taskTypeId,
            scheduleType,
            agentOracleId,
            noRosterIdClassName,
            isNoRosterIdSearch,
          );
        } catch (error) {
          if (!cancelled)
            console.error("Failed to refresh credentials data:", error);
        }
      }
    };

    refreshAfterAttachment();

    return () => {
      cancelled = true;
    };
  }, [openAttachmentDialog]);

  const fetchCredentialsData = async (
    rosterId,
    asdLeadOracleId,
    taskTypeId,
    scheduleType,
    agentOracleId,
    noRosterIdClassName,
    isNoRosterIdSearch,
  ) => {
    setLoader(true);
    try {
      const response = await getOMTCredentials(
        rosterId,
        asdLeadOracleId,
        taskTypeId,
        scheduleType,
        agentOracleId,
        noRosterIdClassName,
        isNoRosterIdSearch,
      );

      if (response?.data?.message === "Success") {
        setCredentialsData(response.data.data);
        setOriginalCredentialsData(
          JSON.parse(JSON.stringify(response.data.data)),
        );
      } else {
        setCredentialsData([]);
        setOriginalCredentialsData([]);
      }
    } catch (error) {
      const is404 =
        error?.response?.status === 404 || error?.statusCode === 404;
      if (is404) {
        setCredentialsData([]);
        setOriginalCredentialsData([]);
        if (rosterId || noRosterIdClassName) {
          errorHandler(
            true,
            "No credentials records found for the selected filters.",
            "info",
          );
        }
      } else {
        errorHandler(true, "Error fetching credentials data", "error");
      }
    }
    setLoader(false);
  };

  const handleChangeChecked = (event, rowIndex) => {
    const { name, checked } = event.target;

    const trainee = CredentialsData.map((item, index) => {
      if (index === rowIndex) {
        const updatedItem = { ...item, [name]: checked };

        //PasswordNotWorking is unchecked, cleared dependent fields
        if (name === "PasswordNotWorking" && !checked) {
          updatedItem.AccountDisabled = false;
          updatedItem.IncorrectProvisioning = false;
          updatedItem.NoADAccount = false;
        }

        return updatedItem;
      }
      return item;
    });

    setCredentialsData(trainee);
  };

  const handleTextValueChange = (event, rowIndex, fieldName) => {
    let trainee = CredentialsData.map((item, index) => {
      if (index === rowIndex)
        return {
          ...item,
          [fieldName]: event.target.value === "" ? null : event.target.value,
        };
      return item;
    });

    setCredentialsData(trainee);
  };

  const handleSave = async () => {
    const hasChanges =
      JSON.stringify(CredentialsData) !==
      JSON.stringify(originalCredentialsData);

    if (!hasChanges) {
      setSnack({ open: true, message: "No changes to save", severity: "info" });
      return true;
    }

    // Validation: PasswordNotWorking is checked and at least one reason must be selected
    const invalidRow = CredentialsData.find(
      (row) =>
        row.PasswordNotWorking &&
        !row.AccountDisabled &&
        !row.IncorrectProvisioning &&
        !row.NoADAccount,
    );
    if (invalidRow) {
      setSnack({
        open: true,
        message: `Please select at least one reason for the password failure (Name: ${invalidRow.EmployeeName})`,
        severity: "error",
      });
      return false;
    }

    setLoader(true);
    try {
      const data = createData();
      if (data.length !== CredentialsData.length) return false;
      const response = await saveOMTCredentials(
        data,
        localStorage.getItem("User"),
      );
      if (response.message === "Success") {
        setSnack({
          open: true,
          message: "All changes have been saved Successfully.",
        });
        await fetchCredentialsData(
          rosterId,
          asdLeadOracleId,
          taskTypeId,
          scheduleType,
          agentOracleId,
          noRosterIdClassName,
          isNoRosterIdSearch,
        );
        return true;
      }
      setSnack({ open: true, message: "Failed to Save..", severity: "error" });
      return false;
    } catch {
      setSnack({
        open: true,
        message: "Error saving Credentials..",
        severity: "error",
      });
      return false;
    } finally {
      setLoader(false);
    }
  };

  const createData = () => {
    const booleanFields = [
      "WindowsLoginFailure",
      "PasswordNotWorking",
      "AccountDisabled",
      "IncorrectProvisioning",
      "NoADAccount",
    ];

    return CredentialsData.map((record) => ({
      OMTCredentialsId: record.OMTCredentialsId || 0,
      OMTClassDetailsId: record.OMTClassDetailsId,
      ClassRosterId: record.ClassRosterId,
      OracleId: record.OracleId,
      EmployeeName: record.EmployeeName,
      TraineeStatus: record.TraineeStatus,
      Comments: record.Comments,
      OMTScheduleStatusType: record.ScheduleType || "",
      ...Object.fromEntries(
        booleanFields.map((field) => [field, record[field] || false]),
      ),
    }));
  };

  const handleReset = async () => {
    fetchCredentialsData(
      rosterId,
      asdLeadOracleId,
      taskTypeId,
      scheduleType,
      agentOracleId,
      noRosterIdClassName,
    );
  };

  const sortByCol = (data, col, direction) =>
    [...data].sort((a, b) => {
      if (typeof a[col] === "string" && typeof b[col] === "string") {
        return direction === "ASC"
          ? a[col].toLowerCase() > b[col].toLowerCase()
            ? 1
            : -1
          : a[col].toLowerCase() < b[col].toLowerCase()
            ? 1
            : -1;
      }
      return direction === "ASC"
        ? a[col] > b[col]
          ? 1
          : -1
        : a[col] < b[col]
          ? 1
          : -1;
    });

  const sorting = (col) => {
    if (order === "ASC") {
      setCredentialsData(sortByCol(CredentialsData, col, "ASC"));
      setOriginalCredentialsData(
        sortByCol(originalCredentialsData, col, "ASC"),
      );
      setOrder("DSC");
    } else if (order === "DSC") {
      setCredentialsData(sortByCol(CredentialsData, col, "DSC"));
      setOriginalCredentialsData(
        sortByCol(originalCredentialsData, col, "DSC"),
      );
      setOrder("ASC");
    }
  };

  const traineeStatusFilter = useColumnFilter(CredentialsData, "TraineeStatus");

  const FilteredCredentialsData = useMemo(() => {
    return CredentialsData.map((row, originalIndex) => ({
      row,
      originalIndex,
    })).filter(({ row }) => traineeStatusFilter.matches(row));
  }, [CredentialsData, traineeStatusFilter.selectedValues]);

  return (
    <>
      {loading ? <BackdropComponent /> : null}
      <Box className={classes.fontStyles}>
        <Paper sx={{ padding: 2 }}>
          <TableContainer className={classes.container}>
            <Table stickyHeader size="small" aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ backgroundColor: "white" }}
                    onClick={() =>
                      sorting(
                        isUploadMode ? "NoRosterIdClassName" : "ClassRosterId",
                      )
                    }
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={isUploadMode ? "Class Name" : "RID"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Name")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Name"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Schedule Type")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Type"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                    onClick={() => sorting("Schedule Status")}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Schedule Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <UnfoldMoreOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "#3F5765",
                          cursor: "pointer",
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      backgroundColor: "white",
                      width: "150px",
                      alignItems: "center",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <TextLabel
                        text={"Trainee Status"}
                        className={`${classes.fontStyles} ${classes.cellPadding}`}
                      />
                      <IconButton
                        onClick={traineeStatusFilter.openFilter}
                        size="small"
                        color={
                          traineeStatusFilter.selectedValues.length
                            ? "primary"
                            : "default"
                        }
                      >
                        <FilterListIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Windows Login Failure"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Password Not Working"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Account Disabled"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Incorrect Provisioning"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"No AD Account Provisioned"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                  <TableCell sx={{ backgroundColor: "white" }}>
                    <TextLabel
                      text={"Comments"}
                      className={`${classes.fontStyles} ${classes.cellPadding}`}
                    />
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {FilteredCredentialsData.map(({ row, originalIndex }) => {
                  const rowKey = `${row.OracleId}-${originalIndex}`;
                  const isDisabled =
                    row.AttendanceNoShow === true ||
                    row.AttendanceISPOutage === true || // disable if no show or ISP outage in attendance tab.
                    isLockedByHardware(row); // disable if hardware issue
                  return (
                    <TableRow key={rowKey}>
                      <TableCell align="center">
                        <Grid container>
                          <Grid item xs={12}>
                            <TextLabel
                              text={
                                noRosterIdClassName
                                  ? row.NoRosterIdClassName
                                  : row.ClassRosterId
                              }
                              fontSize={14}
                              subHeading
                            />
                          </Grid>
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Tooltip title="Attach File">
                              <Badge
                                badgeContent={row?.AttachmentCount || 0}
                                color="primary"
                                sx={{
                                  "& .MuiBadge-badge": {
                                    color: "#ffffff",
                                    backgroundColor: red[700],
                                  },
                                }}
                              >
                                <IconButton
                                  onClick={() => handleAttachmentOpen(row)}
                                  size="small"
                                  disableRipple
                                  disabled={isDisabled}
                                  sx={{
                                    backgroundColor: "#3299ff",
                                    borderRadius: 1,
                                  }}
                                  aria-label="attach file"
                                >
                                  <AttachFileOutlined
                                    fontSize="small"
                                    sx={{ color: "#ffffff" }}
                                  />
                                </IconButton>
                              </Badge>
                            </Tooltip>
                          </Grid>
                        </Grid>
                      </TableCell>
                      <TableCell>{row.EmployeeName}</TableCell>
                      <TableCell>
                        <TextLabel text={row.ScheduleType} description />
                      </TableCell>
                      <TableCell>
                        <TextLabel text={row.ScheduleStatus} description />
                      </TableCell>
                      <TableCell>
                        <TextLabel
                          text={row.TraineeStatus}
                          description
                        ></TextLabel>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="WindowsLoginFailure"
                          checked={row.WindowsLoginFailure}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled}
                          name="PasswordNotWorking"
                          checked={row.PasswordNotWorking}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled || !row.PasswordNotWorking}
                          name="AccountDisabled"
                          checked={row.AccountDisabled}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled || !row.PasswordNotWorking}
                          name="IncorrectProvisioning"
                          checked={row.IncorrectProvisioning}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          type="checkbox"
                          disabled={isDisabled || !row.PasswordNotWorking}
                          name="NoADAccount"
                          checked={row.NoADAccount}
                          onChange={(event) =>
                            handleChangeChecked(event, originalIndex)
                          }
                        />
                      </TableCell>
                      <TableCell className={classes.minWidth}>
                        <CustomTooltip title={row.Comments || ""}>
                          <TextField
                            size="small"
                            disabled={isDisabled}
                            multiline
                            fullWidth
                            maxRows={4}
                            value={row.Comments || ""}
                            onChange={(e) =>
                              handleTextValueChange(
                                e,
                                originalIndex,
                                "Comments",
                              )
                            }
                            InputProps={{ sx: { ...fontStyles } }}
                            helperText={`${row.Comments ? row.Comments.length : 0}/${100}`}
                            inputProps={{ maxLength: 100 }}
                            placeholder="Max 100 Characters"
                          />
                        </CustomTooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ColumnFilterPopover
            anchorEl={traineeStatusFilter.anchorEl}
            onClose={traineeStatusFilter.closeFilter}
            options={traineeStatusFilter.uniqueValues}
            selectedValues={traineeStatusFilter.selectedValues}
            onToggle={traineeStatusFilter.toggleValue}
            onClear={traineeStatusFilter.clearFilter}
          />
          <Box className={classes.buttonBox}>
            <CustomButton
              variant="contained"
              className={classes.buttonSave}
              size="small"
              onClick={handleSave}
              disabled={!editAccess}
            >
              Save
            </CustomButton>
            <CustomButton
              variant="outlined"
              className={classes.buttonReset}
              size="small"
              onClick={handleReset}
              disabled={!editAccess}
            >
              Reset
            </CustomButton>
          </Box>
        </Paper>
      </Box>
      <SnackBar snack={snack} snackReset={snackReset} />
      {openAttachmentDialog && (
        <OMTAttachments
          openAttachmentDialog={openAttachmentDialog}
          row={selectedRow}
          category="AD Credentials"
          onClose={() => setOpenAttachmentDialog(false)}
          editAccess={editAccess}
        />
      )}
    </>
  );
});

Credentials.propTypes = {
  rosterId: PropTypes.string,
  asdLeadOracleId: PropTypes.string,
  taskTypeId: PropTypes.string,
  scheduleType: PropTypes.string,
  editAccess: PropTypes.bool,
  agentOracleId: PropTypes.string,
  onDirtyChange: PropTypes.func,
};

export default Credentials;
